import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Alert, Button, Card, Col, DatePicker, Divider, Form, Input, InputNumber, message, Modal, Popconfirm, Row, Space, Table, Tabs, Tag, Typography, Upload } from "antd";
import { CalculatorOutlined, DownloadOutlined, EyeInvisibleOutlined, EyeOutlined, FolderOpenOutlined, PlusOutlined, ReloadOutlined, SaveOutlined, UploadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import designerBonusService from "../../services/designerBonusService";
import {
  applyDefaultCoefficientsToBonusData,
  calculateDesignerMonthBonus,
  calculateMarketDesigner,
  calculateMarketMonthBonus,
  calculateMarketMonthResult,
  CALCULATION_POLICY_VERSION,
  filterRowsWithBonus,
  MONTHLY_THRESHOLD_POLICY_VERSION,
  ORDER_COUNT_POLICY_VERSION,
} from "./designerBonusCalculation";
import "./DesignerBonus.css";

const { Title, Text } = Typography;
const MARKETS = ["FBA", "FBM", "Website", "Etsy"];
const SAVED_CALCULATION_SCHEMA_VERSION = 1;

const DEFAULT_SETTINGS = {
  thresholds: {
    FBA: 500,
    FBM: 500,
    Website: 500,
    Etsy: 500,
  },
  defaultCoefficients: {
    FBA: 1,
    FBM: 1,
    Website: 1,
    Etsy: 1,
  },
  exchangeRate: 25000,
};

function todayString() {
  return new Date().toISOString().slice(0, 10);
}

function firstDayOfMonthString() {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1)).toISOString().slice(0, 10);
}

function currency(value) {
  return Number(value || 0).toLocaleString("vi-VN");
}

function numberFormat(value) {
  return Number(value || 0).toLocaleString("vi-VN");
}

function moneyVnd(value) {
  return `${currency(value)} VNĐ`;
}

function monthRange(startDate, endDate) {
  if (!startDate || !endDate) return [];

  const start = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);
  const months = [];
  const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
  const endCursor = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));

  while (cursor <= endCursor) {
    months.push(cursor.toISOString().slice(0, 7));
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }

  return months;
}

function emptyCell() {
  return {
    sourceSale: 0,
    sale: 0,
    coefficient: 0,
    orderStats: { total: 0, canceled: 0, refunded: 0, replacement: 0 },
    quantityStats: {
      orders: 0,
      grossOrderedQuantity: 0,
      eligibleSaleQuantity: 0,
      canceledQuantity: 0,
      refundedQuantity: 0,
      replacementQuantity: 0,
      excludedQuantity: 0,
    },
  };
}

function normalizeSettings(settings) {
  const thresholds = settings?.thresholds || {};
  const defaultCoefficients = settings?.defaultCoefficients || settings?.coefficients || {};
  return {
    thresholds: {
      FBA: Number(thresholds.FBA ?? DEFAULT_SETTINGS.thresholds.FBA),
      FBM: Number(thresholds.FBM ?? DEFAULT_SETTINGS.thresholds.FBM),
      Website: Number(thresholds.Website ?? DEFAULT_SETTINGS.thresholds.Website),
      Etsy: Number(thresholds.Etsy ?? DEFAULT_SETTINGS.thresholds.Etsy),
    },
    defaultCoefficients: {
      FBA: Number(defaultCoefficients.FBA ?? DEFAULT_SETTINGS.defaultCoefficients.FBA),
      FBM: Number(defaultCoefficients.FBM ?? DEFAULT_SETTINGS.defaultCoefficients.FBM),
      Website: Number(defaultCoefficients.Website ?? DEFAULT_SETTINGS.defaultCoefficients.Website),
      Etsy: Number(defaultCoefficients.Etsy ?? DEFAULT_SETTINGS.defaultCoefficients.Etsy),
    },
    exchangeRate: Number(settings?.exchangeRate ?? DEFAULT_SETTINGS.exchangeRate),
  };
}

function mergeMarketData(previousData, market, rows = {}, defaultCoefficient = 1) {
  const nextData = { ...previousData };

  Object.entries(rows).forEach(([designerCode, marketData]) => {
    nextData[designerCode] = nextData[designerCode] || {};
    nextData[designerCode][market] = nextData[designerCode][market] || {};

    const salesByMonth = marketData?.sales || marketData || {};
    const orderStatsByMonth = marketData?.orderStats || {};
    const quantityStatsByMonth = marketData?.quantityStats || {};
    const monthKeys = new Set([...Object.keys(salesByMonth), ...Object.keys(orderStatsByMonth), ...Object.keys(quantityStatsByMonth)]);
    monthKeys.forEach((monthKey) => {
      const sale = salesByMonth[monthKey] || 0;
      const existingCell = nextData[designerCode][market][monthKey];
      const currentCell = existingCell || emptyCell();
      nextData[designerCode][market][monthKey] = {
        ...currentCell,
        sourceSale: Number(sale),
        sale: Number(sale),
        coefficient: existingCell && existingCell.coefficient !== undefined
          ? currentCell.coefficient
          : defaultCoefficient,
        orderStats: {
          ...emptyCell().orderStats,
          ...(orderStatsByMonth[monthKey] || currentCell.orderStats || {}),
        },
        quantityStats: {
          ...emptyCell().quantityStats,
          ...(quantityStatsByMonth[monthKey] || currentCell.quantityStats || {}),
        },
      };
    });
  });

  return nextData;
}

function responseMarketToRows(summary, market) {
  const rows = {};
  const marketRows = summary?.markets?.[market] || [];

  marketRows.forEach((item) => {
    rows[item.designerCode] = {
      sales: item.sales || {},
      orderStats: item.orderStats || {},
      quantityStats: item.quantityStats || {},
    };
  });

  return rows;
}

function getCell(data, designerCode, market, monthKey) {
  return data?.[designerCode]?.[market]?.[monthKey] || emptyCell();
}

function getQuantityStats(data, designerCode, market, monthKey) {
  return getCell(data, designerCode, market, monthKey).quantityStats || emptyCell().quantityStats;
}

function getDesignerQuantityStats(data, designerCode, monthKey) {
  return MARKETS.reduce(
    (totals, market) => {
      const stats = getQuantityStats(data, designerCode, market, monthKey);
      return {
        orders: totals.orders + Number(stats.orders || 0),
        grossOrderedQuantity: totals.grossOrderedQuantity + Number(stats.grossOrderedQuantity || 0),
        eligibleSaleQuantity: totals.eligibleSaleQuantity + Number(stats.eligibleSaleQuantity || 0),
        canceledQuantity: totals.canceledQuantity + Number(stats.canceledQuantity || 0),
        refundedQuantity: totals.refundedQuantity + Number(stats.refundedQuantity || 0),
        replacementQuantity: totals.replacementQuantity + Number(stats.replacementQuantity || 0),
        excludedQuantity: totals.excludedQuantity + Number(stats.excludedQuantity || 0),
      };
    },
    {
      orders: 0,
      grossOrderedQuantity: 0,
      eligibleSaleQuantity: 0,
      canceledQuantity: 0,
      refundedQuantity: 0,
      replacementQuantity: 0,
      excludedQuantity: 0,
    },
  );
}

const MAX_ETSY_ROWS = 30000;
const ETSY_PROCESS_BATCH_SIZE = 1000;
const CANCELED_ETSY_STATUSES = new Set(["canceled", "cancelled"]);
const REFUNDED_ETSY_STATUSES = new Set(["refunded", "partially_refunded", "voided"]);

function normalizeEtsyHeader(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function getEtsyColumnIndex(headers, name) {
  const names = Array.isArray(name) ? name : [name];
  return headers.findIndex((header) => names.some((candidate) => normalizeEtsyHeader(header) === normalizeEtsyHeader(candidate)));
}

function parseEtsyDate(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
  }

  if (typeof value === "number") {
    const parsed = XLSX.SSF.parse_date_code(value);
    return parsed ? new Date(Date.UTC(parsed.y, parsed.m - 1, parsed.d)) : null;
  }

  const match = String(value || "")
    .trim()
    .match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? date : null;
}

function isEmptyEtsyRow(row) {
  return !row || row.every((cell) => String(cell || "").trim() === "");
}

function yieldToBrowser() {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

async function parseEtsyFile(file, startDate, endDate, onProgress, isCancelled) {
  if (!file) throw new Error("Please select an Etsy Excel file");

  onProgress("Reading file");
  const arrayBuffer = await file.arrayBuffer();
  if (isCancelled()) return null;

  // Keep date cells as Excel serials. Converting them to JS Date objects can
  // introduce a timezone/time-of-day shift before we apply the date filter.
  const workbook = XLSX.read(arrayBuffer, { type: "array", cellDates: false });
  const sheetName = workbook.SheetNames.find((name) => name.trim().toLowerCase() === "order");
  if (!sheetName) throw new Error('Cannot find sheet "Order" in Etsy file');

  const matrix = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
    header: 1,
    raw: true,
    blankrows: false,
  });
  if (!matrix.length) throw new Error("Sheet Order is empty");

  const headers = matrix[0] || [];
  const dateIndex = getEtsyColumnIndex(headers, "Date");
  const orderIdIndex = getEtsyColumnIndex(headers, ["Order ID", "Order ID Etsy"]);
  const skuIndex = getEtsyColumnIndex(headers, "SKU");
  const quantityIndex = getEtsyColumnIndex(headers, "Quantity");
  const statusIndex = getEtsyColumnIndex(headers, "Tracking Status");
  if ([dateIndex, orderIdIndex, skuIndex, quantityIndex, statusIndex].some((index) => index < 0)) {
    throw new Error("Sheet Order must include Date, Order ID, SKU, Quantity and Tracking Status columns");
  }

  const dataRows = matrix.slice(1).filter((row) => !isEmptyEtsyRow(row));
  if (dataRows.length > MAX_ETSY_ROWS) {
    throw new Error(`Etsy file has ${dataRows.length} rows. Maximum supported rows is ${MAX_ETSY_ROWS}.`);
  }

  const compactMap = new Map();
  let skippedByDate = 0;
  let skippedByStatus = 0;
  let invalidRows = 0;
  let validRows = 0;

  onProgress("Processing rows", { sourceRowCount: dataRows.length });
  for (let offset = 0; offset < dataRows.length; offset += ETSY_PROCESS_BATCH_SIZE) {
    if (isCancelled()) return null;
    const end = Math.min(offset + ETSY_PROCESS_BATCH_SIZE, dataRows.length);

    for (let index = offset; index < end; index += 1) {
      const row = dataRows[index];
      const parsedDate = parseEtsyDate(row[dateIndex]);
      const orderId = String(row[orderIdIndex] || "").trim();
      const sku = String(row[skuIndex] || "")
        .trim()
        .toUpperCase();
      const quantity = Number(row[quantityIndex]);
      const rawStatus = String(row[statusIndex] || "")
        .trim()
        .toLowerCase();
      const status = CANCELED_ETSY_STATUSES.has(rawStatus) ? "canceled" : REFUNDED_ETSY_STATUSES.has(rawStatus) ? "refunded" : "completed";

      if (!parsedDate) {
        invalidRows += 1;
        continue;
      }

      const dateOnly = [parsedDate.getUTCFullYear(), String(parsedDate.getUTCMonth() + 1).padStart(2, "0"), String(parsedDate.getUTCDate()).padStart(2, "0")].join("-");
      if (dateOnly < startDate || dateOnly > endDate) {
        skippedByDate += 1;
        continue;
      }
      if (!orderId || !sku || !Number.isInteger(quantity) || quantity <= 0) {
        invalidRows += 1;
        continue;
      }

      validRows += 1;
      const monthKey = dateOnly.slice(0, 7);
      const key = `${monthKey}|${sku}|${orderId}|${status}`;
      compactMap.set(key, (compactMap.get(key) || 0) + quantity);
    }

    onProgress("Processing rows", {
      sourceRowCount: dataRows.length,
      processedRows: end,
      validRows,
    });
    if (end < dataRows.length) await yieldToBrowser();
  }

  const rows = Array.from(compactMap.entries()).map(([key, quantity]) => {
    const [monthKey, sku, orderId, status] = key.split("|");
    return { monthKey, sku, orderId, status, quantity };
  });

  return {
    sourceRowCount: dataRows.length,
    rows,
    stats: { validRows, skippedByDate, skippedByStatus, invalidRows, compactRowCount: rows.length },
  };
}

function buildCalculationData({ startDate, endDate, settings, bonusData, sourceStatus, calculationPolicyVersion }) {
  return {
    schemaVersion: SAVED_CALCULATION_SCHEMA_VERSION,
    calculationPolicyVersion,
    startDate,
    endDate,
    settings,
    bonusData,
    sourceStatus,
  };
}

function SavedCalculationTables({ data, showQuantityStats = false }) {
  const savedBonusData = data?.bonusData || {};
  const savedSettings = normalizeSettings(data?.settings);
  const savedPolicyVersion = Number(data?.calculationPolicyVersion || 1);
  const hasCurrentOrderCount = savedPolicyVersion >= ORDER_COUNT_POLICY_VERSION;
  const months = useMemo(() => {
    const monthSet = new Set(monthRange(data?.startDate, data?.endDate));
    Object.values(savedBonusData).forEach((markets) => {
      Object.values(markets || {}).forEach((cells) => {
        Object.keys(cells || {}).forEach((monthKey) => monthSet.add(monthKey));
      });
    });
    return Array.from(monthSet).sort();
  }, [data?.startDate, data?.endDate, savedBonusData]);
  const designers = useMemo(() => Object.keys(savedBonusData).sort(), [savedBonusData]);

  const allSummaryRows = useMemo(() => designers.map((designerCode) => {
    const row = { designerCode };
    let totalSale = 0;
    let totalBonus = 0;

    months.forEach((monthKey) => {
      row[`${monthKey}Bonus`] = calculateDesignerMonthBonus(
        savedBonusData,
        designerCode,
        monthKey,
        months,
        savedSettings,
        MARKETS,
        savedPolicyVersion,
      );
    });

    MARKETS.forEach((market) => {
      const result = calculateMarketDesigner(savedBonusData, designerCode, market, months, savedSettings, savedPolicyVersion);
      row[`${market}Sale`] = result.periodSale;
      row[`${market}EligibleSale`] = result.periodEligibleSale;
      row[`${market}EligibleMonthCount`] = result.eligibleMonthCount;
      row[`${market}Bonus`] = result.bonus;
      row[`${market}Eligible`] = result.eligible;
      totalSale += result.periodSale;
      totalBonus += result.bonus;
    });

    row.totalSale = totalSale;
    row.totalBonus = totalBonus;
    return row;
  }), [designers, months, savedBonusData, savedSettings, savedPolicyVersion]);

  const summaryRows = useMemo(
    () => filterRowsWithBonus(allSummaryRows),
    [allSummaryRows],
  );
  const visibleDesignerCodes = useMemo(
    () => new Set(summaryRows.map((row) => row.designerCode)),
    [summaryRows],
  );

  const summaryTotals = useMemo(() => {
    const totals = { totalSale: 0, totalBonus: 0 };
    months.forEach((monthKey) => { totals[`${monthKey}Bonus`] = 0; });
    summaryRows.forEach((row) => {
      totals.totalSale += Number(row.totalSale || 0);
      totals.totalBonus += Number(row.totalBonus || 0);
      months.forEach((monthKey) => {
        totals[`${monthKey}Bonus`] += Number(row[`${monthKey}Bonus`] || 0);
      });
    });
    return totals;
  }, [summaryRows, months]);

  const marketColumns = (market) => [
    {
      title: "Designer",
      dataIndex: "designerCode",
      fixed: "left",
      width: 96,
      render: (value) => <Tag color="blue">{value}</Tag>,
    },
    ...months.map((monthKey) => ({
      title: (
        <Space direction="vertical" size={2} style={{ width: "100%" }}>
          <Text strong>{monthKey}</Text>
          <div className="designer-bonus-cell-labels"><span>Sale</span><span>Coefficient</span></div>
        </Space>
      ),
      dataIndex: monthKey,
      width: 270,
      render: (_, record) => {
        const cell = getCell(savedBonusData, record.designerCode, market, monthKey);
        const quantityStats = getQuantityStats(savedBonusData, record.designerCode, market, monthKey);
        const monthResult = calculateMarketMonthResult(
          savedBonusData,
          record.designerCode,
          market,
          monthKey,
          months,
          savedSettings,
          savedPolicyVersion,
        );
        return (
          <div>
            <div className="designer-bonus-cell">
              <InputNumber disabled min={0} precision={0} value={cell.sale} />
              <InputNumber disabled min={0} precision={2} value={cell.coefficient} />
            </div>
            {savedPolicyVersion >= MONTHLY_THRESHOLD_POLICY_VERSION && (
              <div className="designer-bonus-quantity-stats">
                <Tag color={monthResult.eligibleSale > 0 ? "green" : "default"}>Bonus Units {numberFormat(monthResult.eligibleSale)}</Tag>
              </div>
            )}
            {showQuantityStats && (
              <div className="designer-bonus-quantity-stats">
                <Tag color="blue">Total Orders {hasCurrentOrderCount ? numberFormat(quantityStats.orders) : "N/A"}</Tag>
                <Tag>Total Quantity {numberFormat(quantityStats.grossOrderedQuantity)}</Tag>
                <Tag color="green">Eligible Quantity {numberFormat(quantityStats.eligibleSaleQuantity)}</Tag>
              </div>
            )}
          </div>
        );
      },
    })),
    {
      title: "Period Sale",
      width: 110,
      render: (_, record) => calculateMarketDesigner(savedBonusData, record.designerCode, market, months, savedSettings, savedPolicyVersion).periodSale,
    },
    {
      title: "Bonus Units",
      width: 110,
      render: (_, record) => calculateMarketDesigner(savedBonusData, record.designerCode, market, months, savedSettings, savedPolicyVersion).periodEligibleSale,
    },
    {
      title: "Status",
      width: 120,
      render: (_, record) => {
        const result = calculateMarketDesigner(savedBonusData, record.designerCode, market, months, savedSettings, savedPolicyVersion);
        return result.eligible
          ? <Tag color="green">{savedPolicyVersion >= MONTHLY_THRESHOLD_POLICY_VERSION ? `${result.eligibleMonthCount}/${months.length} months` : "Bonus (legacy)"}</Tag>
          : <Tag>0</Tag>;
      },
    },
    {
      title: "Bonus (VNĐ)",
      width: 130,
      align: "right",
      render: (_, record) => currency(calculateMarketDesigner(savedBonusData, record.designerCode, market, months, savedSettings, savedPolicyVersion).bonus),
    },
  ];

  const summaryColumns = [
    {
      title: "Designer",
      dataIndex: "designerCode",
      fixed: "left",
      width: 110,
      render: (value) => <Tag color="blue">{value}</Tag>,
    },
    { title: "Total Sale", dataIndex: "totalSale", align: "right", width: 110, render: numberFormat },
    ...months.map((monthKey) => ({
      title: monthKey,
      align: "right",
      width: 160,
      render: (_, record) => currency(record[`${monthKey}Bonus`]),
    })),
    { title: "Total Payout (VNĐ)", dataIndex: "totalBonus", align: "right", fixed: "right", width: 160, render: currency },
  ];

  const renderBreakdown = (record) => {
    const columns = [
      { title: "Market", dataIndex: "market", fixed: "left", width: 120 },
      ...months.map((monthKey) => ({
        title: monthKey,
        align: "right",
        width: 140,
        render: (_, row) => currency(row[`${monthKey}Bonus`]),
      })),
      { title: "Total", dataIndex: "totalBonus", align: "right", fixed: "right", width: 150, render: currency },
    ];
    const rows = MARKETS.map((market) => {
      const row = { key: `${record.designerCode}-${market}`, market, totalBonus: record[`${market}Bonus`] };
      months.forEach((monthKey) => {
        row[`${monthKey}Bonus`] = calculateMarketMonthBonus(savedBonusData, record.designerCode, market, monthKey, months, savedSettings, savedPolicyVersion);
      });
      return row;
    });
    return <Table size="small" rowKey="key" columns={columns} dataSource={rows} pagination={false} scroll={{ x: Math.max(760, months.length * 140 + 270) }} />;
  };

  return (
    <>
      <Tabs
        items={MARKETS.map((market) => ({
          key: market,
          label: market,
          children: (
            <Card className="designer-bonus-panel" bordered={false}>
              <Table
                className="designer-bonus-table"
                size="small"
                rowKey="key"
                columns={marketColumns(market)}
                dataSource={designers
                  .filter((designerCode) => visibleDesignerCodes.has(designerCode))
                  .filter((designerCode) => savedBonusData[designerCode]?.[market])
                  .map((designerCode) => ({ key: `${market}-${designerCode}`, designerCode }))}
                locale={{ emptyText: "No designer exceeded the monthly threshold" }}
                pagination={{ pageSize: 20 }}
                scroll={{ x: Math.max(1100, months.length * 270 + 420) }}
              />
            </Card>
          ),
        }))}
      />
      <Card className="designer-bonus-panel" bordered={false} title="Designer total">
        <Table
          size="small"
          rowKey="designerCode"
          columns={summaryColumns}
          dataSource={summaryRows}
          locale={{ emptyText: "No designer exceeded the monthly threshold" }}
          expandable={{ expandedRowRender: renderBreakdown, expandRowByClick: true }}
          pagination={{ pageSize: 20 }}
          scroll={{ x: Math.max(1100, months.length * 160 + 380) }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}><Text strong>Total payout by month</Text></Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right"><Text strong>{numberFormat(summaryTotals.totalSale)}</Text></Table.Summary.Cell>
                {months.map((monthKey, index) => <Table.Summary.Cell key={monthKey} index={index + 3} align="right"><Text strong>{moneyVnd(summaryTotals[`${monthKey}Bonus`])}</Text></Table.Summary.Cell>)}
                <Table.Summary.Cell index={months.length + 2} align="right"><Text strong>{moneyVnd(summaryTotals.totalBonus)}</Text></Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Card>
    </>
  );
}

const SaveCalculationModal = React.memo(function SaveCalculationModal({
  open,
  initialName,
  initialDescription,
  loading,
  existing,
  onCancel,
  onSave,
}) {
  const [name, setName] = useState(initialName || "");
  const [description, setDescription] = useState(initialDescription || "");

  useEffect(() => {
    if (!open) return;
    setName(initialName || "");
    setDescription(initialDescription || "");
  }, [open, initialName, initialDescription]);

  return (
    <Modal
      open={open}
      title={existing ? "Save Calculation" : "Save New Calculation"}
      onCancel={onCancel}
      onOk={() => onSave({ name, description })}
      okText="Save"
      cancelText="Cancel"
      confirmLoading={loading}
    >
      <Form layout="vertical">
        <Form.Item label="Name" required>
          <Input
            maxLength={255}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Designer Bonus June - Review"
          />
        </Form.Item>
        <Form.Item label="Description">
          <Input.TextArea
            rows={4}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Optional note"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
});

function DesignerBonus() {
  const currentYear = dayjs().year();
  const currentUser = useSelector((state) => state.auth.user);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [startDate, setStartDate] = useState(firstDayOfMonthString());
  const [endDate, setEndDate] = useState(todayString());
  const [bonusData, setBonusData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingCalculation, setSavingCalculation] = useState(false);
  const [submittingCalculation, setSubmittingCalculation] = useState(false);
  const [approvingCalculation, setApprovingCalculation] = useState(false);
  const [sourceStatus, setSourceStatus] = useState({});
  const [etsyProgress, setEtsyProgress] = useState(null);
  const [showQuantityStats, setShowQuantityStats] = useState(false);
  const [savedCalculation, setSavedCalculation] = useState(null);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [savedCalculationsLoading, setSavedCalculationsLoading] = useState(false);
  const [savedModalOpen, setSavedModalOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewCalculation, setPreviewCalculation] = useState(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [calculationPolicyVersion, setCalculationPolicyVersion] = useState(CALCULATION_POLICY_VERSION);
  const importRunRef = useRef(0);

  const isReadOnly = Boolean(savedCalculation && savedCalculation.status !== "DRAFT");
  const currentRoles = [currentUser?.role, currentUser?.userRole?.name].filter(Boolean);
  const canApprove = currentRoles.some((roleName) => ["Admin", "Super Admin", "Manager"].includes(roleName));

  useEffect(() => {
    designerBonusService
      .getSettings()
      .then((response) => setSettings(normalizeSettings(response.data?.data)))
      .catch(() => message.error("Cannot load designer bonus settings"));

    return undefined;
  }, []);

  useEffect(() => {
    if (!startDate || !endDate || startDate > endDate) return;
    const allowedMonths = new Set(monthRange(startDate, endDate));
    setBonusData((current) => {
      let changed = false;
      const next = {};

      Object.entries(current).forEach(([designerCode, markets]) => {
        const nextMarkets = {};
        Object.entries(markets || {}).forEach(([market, cells]) => {
          const nextCells = Object.fromEntries(Object.entries(cells || {}).filter(([monthKey]) => allowedMonths.has(monthKey)));
          if (Object.keys(nextCells).length !== Object.keys(cells || {}).length) changed = true;
          if (Object.keys(nextCells).length) nextMarkets[market] = nextCells;
        });
        if (Object.keys(nextMarkets).length !== Object.keys(markets || {}).length) changed = true;
        if (Object.keys(nextMarkets).length) next[designerCode] = nextMarkets;
      });

      return changed ? next : current;
    });
  }, [startDate, endDate]);

  const months = useMemo(() => {
    return monthRange(startDate, endDate);
  }, [startDate, endDate]);

  const designers = useMemo(() => Object.keys(bonusData).sort(), [bonusData]);

  const allSummaryRows = useMemo(
    () =>
      designers.map((designerCode) => {
        const row = { designerCode };
        let totalSale = 0;
        let totalBonus = 0;

        months.forEach((monthKey) => {
          row[`${monthKey}Bonus`] = calculateDesignerMonthBonus(
            bonusData,
            designerCode,
            monthKey,
            months,
            settings,
            MARKETS,
            calculationPolicyVersion,
          );
        });

        MARKETS.forEach((market) => {
          const result = calculateMarketDesigner(bonusData, designerCode, market, months, settings, calculationPolicyVersion);
          row[`${market}Sale`] = result.periodSale;
          row[`${market}EligibleSale`] = result.periodEligibleSale;
          row[`${market}EligibleMonthCount`] = result.eligibleMonthCount;
          row[`${market}Bonus`] = result.bonus;
          row[`${market}Eligible`] = result.eligible;
          totalSale += result.periodSale;
          totalBonus += result.bonus;
        });

        row.totalSale = totalSale;
        row.totalBonus = totalBonus;
        return row;
      }),
    [bonusData, designers, months, settings, calculationPolicyVersion],
  );

  const summaryRows = useMemo(
    () => filterRowsWithBonus(allSummaryRows),
    [allSummaryRows],
  );
  const visibleDesignerCodes = useMemo(
    () => new Set(summaryRows.map((row) => row.designerCode)),
    [summaryRows],
  );

  const summaryTotals = useMemo(() => {
    const totals = {
      totalSale: 0,
      totalBonus: 0,
    };

    months.forEach((monthKey) => {
      totals[`${monthKey}Bonus`] = 0;
    });

    summaryRows.forEach((row) => {
      totals.totalSale += Number(row.totalSale || 0);
      totals.totalBonus += Number(row.totalBonus || 0);
      months.forEach((monthKey) => {
        totals[`${monthKey}Bonus`] += Number(row[`${monthKey}Bonus`] || 0);
      });
    });

    return totals;
  }, [summaryRows, months]);

  const totalBonus = summaryTotals.totalBonus;
  const totalSales = useMemo(
    () => allSummaryRows.reduce((sum, row) => sum + Number(row.totalSale || 0), 0),
    [allSummaryRows],
  );
  const highestDesigner = summaryRows.reduce((best, row) => (!best || row.totalBonus > best.totalBonus ? row : best), null);

  const loadSavedCalculations = async () => {
    try {
      setSavedCalculationsLoading(true);
      const response = await designerBonusService.listSavedCalculations({ page: 1, pageSize: 100 });
      setSavedCalculations(response.data?.data?.rows || []);
    } catch (error) {
      message.error(error.response?.data?.message || "Cannot load saved calculations");
    } finally {
      setSavedCalculationsLoading(false);
    }
  };

  const openSavedCalculations = async () => {
    setSavedModalOpen(true);
    setPreviewCalculation(null);
    await loadSavedCalculations();
  };

  const selectSavedCalculation = async (record) => {
    try {
      setPreviewLoading(true);
      const response = await designerBonusService.getSavedCalculation(record.id);
      setPreviewCalculation(response.data?.data || null);
    } catch (error) {
      message.error(error.response?.data?.message || "Cannot open saved calculation");
    } finally {
      setPreviewLoading(false);
    }
  };

  const applySavedCalculation = (detail) => {
    const data = detail?.data;
    if (!data) {
      message.error("Saved calculation data is empty");
      return;
    }
    setStartDate(data.startDate || "");
    setEndDate(data.endDate || "");
    setSettings(normalizeSettings(data.settings));
    setBonusData(data.bonusData || {});
    setSourceStatus(data.sourceStatus || {});
    setCalculationPolicyVersion(Number(data.calculationPolicyVersion || 1));
    setSavedCalculation(detail);
  };

  const openPreviewForEdit = () => {
    if (!previewCalculation || previewCalculation.status !== "DRAFT") return;
    const savedPolicyVersion = Number(previewCalculation.data?.calculationPolicyVersion || 1);
    const openCalculation = (detail) => {
      applySavedCalculation(detail);
      setSavedModalOpen(false);
    };

    if (savedPolicyVersion < MONTHLY_THRESHOLD_POLICY_VERSION) {
      Modal.confirm({
        title: "Upgrade this draft to the monthly threshold policy?",
        content: "The bonus amount will be recalculated. Submitted and approved snapshots are not affected.",
        okText: "Upgrade and Open",
        cancelText: "Cancel",
        onOk: () => openCalculation({
          ...previewCalculation,
          data: {
            ...previewCalculation.data,
            calculationPolicyVersion: CALCULATION_POLICY_VERSION,
          },
        }),
      });
      return;
    }

    openCalculation(previewCalculation);
  };

  const newCalculation = () => {
    const hasWorkingData = Boolean(savedCalculation || Object.keys(bonusData).length);
    const start = () => {
      setSavedCalculation(null);
      setBonusData({});
      setSourceStatus({});
      setStartDate(firstDayOfMonthString());
      setEndDate(todayString());
      setEtsyProgress(null);
      setCalculationPolicyVersion(CALCULATION_POLICY_VERSION);
    };

    if (!hasWorkingData) {
      start();
      return;
    }

    Modal.confirm({
      title: "Start a new calculation?",
      content: "Unsaved working data will no longer be shown on this screen.",
      okText: "New Calculation",
      cancelText: "Cancel",
      onOk: start,
    });
  };

  const openSaveDialog = () => {
    if (isReadOnly) return;
    setSaveModalOpen(true);
  };

  const saveCalculation = async ({ name, description }) => {
    try {
      setSavingCalculation(true);
      const payload = {
        name,
        description,
        schemaVersion: SAVED_CALCULATION_SCHEMA_VERSION,
        data: buildCalculationData({
          startDate,
          endDate,
          settings,
          bonusData,
          sourceStatus,
          calculationPolicyVersion,
        }),
      };
      const response = savedCalculation?.id
        ? await designerBonusService.updateSavedCalculation(savedCalculation.id, payload)
        : await designerBonusService.createSavedCalculation(payload);
      const detail = response.data?.data;
      setSavedCalculation(detail);
      setSaveModalOpen(false);
      await loadSavedCalculations();
      message.success(savedCalculation?.id ? "Calculation saved" : "Calculation created");
    } catch (error) {
      message.error(error.response?.data?.message || "Cannot save calculation");
    } finally {
      setSavingCalculation(false);
    }
  };

  const submitCalculation = () => {
    if (!savedCalculation?.id || savedCalculation.status !== "DRAFT") return;
    Modal.confirm({
      title: "Submit snapshot for approval?",
      content: "After submitting, this calculation cannot be edited.",
      okText: "Submit Snapshot",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setSubmittingCalculation(true);
          const response = await designerBonusService.submitSavedCalculation(savedCalculation.id);
          const detail = response.data?.data;
          setSavedCalculation(detail);
          await loadSavedCalculations();
          message.success("Snapshot submitted for approval");
        } catch (error) {
          message.error(error.response?.data?.message || "Cannot submit snapshot");
        } finally {
          setSubmittingCalculation(false);
        }
      },
    });
  };

  const approvePreviewCalculation = async () => {
    if (!previewCalculation?.id || previewCalculation.status !== "PENDING_APPROVAL" || !canApprove) return;
    try {
      setApprovingCalculation(true);
      const response = await designerBonusService.approveSavedCalculation(previewCalculation.id);
      const detail = response.data?.data;
      setPreviewCalculation(detail);
      if (savedCalculation?.id === detail.id) setSavedCalculation(detail);
      await loadSavedCalculations();
      message.success("Snapshot approved");
    } catch (error) {
      message.error(error.response?.data?.message || "Cannot approve snapshot");
    } finally {
      setApprovingCalculation(false);
    }
  };

  const updateSetting = (path, value) => {
    if (isReadOnly) return;
    setSettings((current) => {
      if (path === "exchangeRate") {
        return { ...current, exchangeRate: Number(value || 0) };
      }
      return {
        ...current,
        thresholds: {
          ...current.thresholds,
          [path]: Number(value || 0),
        },
      };
    });
  };

  const updateDefaultCoefficient = (market, value) => {
    if (isReadOnly) return;
    setSettings((current) => ({
      ...current,
      defaultCoefficients: {
        ...current.defaultCoefficients,
        [market]: Number(value || 0),
      },
    }));
  };

  const saveSettings = async () => {
    if (isReadOnly) return;
    try {
      setSaving(true);
      const response = await designerBonusService.saveSettings(settings);
      const savedSettings = normalizeSettings(response.data?.data);
      setSettings(savedSettings);
      setBonusData((current) => applyDefaultCoefficientsToBonusData(current, savedSettings.defaultCoefficients));
      message.success(Object.keys(bonusData).length
        ? "Settings saved. Coefficients in the loaded data were updated."
        : "Settings saved");
    } catch (error) {
      message.error(error.response?.data?.message || "Cannot save settings");
    } finally {
      setSaving(false);
    }
  };

  const loadSales = async () => {
    if (isReadOnly) return;
    try {
      setLoading(true);
      setSourceStatus({ Amazon: "loading", Website: "loading" });

      const [amazonResponse, websiteResponse] = await Promise.all([designerBonusService.getAmazonSummary({ startDate, endDate }), designerBonusService.getWebsiteSummary({ startDate, endDate })]);

      setBonusData((current) => {
        let next = mergeMarketData(current, "FBA", responseMarketToRows(amazonResponse.data?.data, "FBA"), settings.defaultCoefficients.FBA);
        next = mergeMarketData(next, "FBM", responseMarketToRows(amazonResponse.data?.data, "FBM"), settings.defaultCoefficients.FBM);
        next = mergeMarketData(next, "Website", responseMarketToRows(websiteResponse.data?.data, "Website"), settings.defaultCoefficients.Website);
        return next;
      });

      setCalculationPolicyVersion(CALCULATION_POLICY_VERSION);
      setSourceStatus({ Amazon: "ready", Website: "ready" });
      message.success("Sales loaded");
    } catch (error) {
      setSourceStatus({ Amazon: "error", Website: "error" });
      message.error(error.response?.data?.message || "Cannot load sales summary");
    } finally {
      setLoading(false);
    }
  };

  const updateCell = (designerCode, market, monthKey, field, value) => {
    if (isReadOnly) return;
    setBonusData((current) => ({
      ...current,
      [designerCode]: {
        ...(current[designerCode] || {}),
        [market]: {
          ...(current[designerCode]?.[market] || {}),
          [monthKey]: {
            ...getCell(current, designerCode, market, monthKey),
            [field]: Number(value || 0),
          },
        },
      },
    }));
  };

  const resetMarket = (market) => {
    if (isReadOnly) return;
    setBonusData((current) => {
      const next = { ...current };
      Object.keys(next).forEach((designerCode) => {
        if (!next[designerCode]?.[market]) return;
        next[designerCode] = { ...next[designerCode], [market]: { ...next[designerCode][market] } };
        Object.keys(next[designerCode][market]).forEach((monthKey) => {
          const cell = next[designerCode][market][monthKey];
          next[designerCode][market][monthKey] = { ...cell, sale: cell.sourceSale };
        });
      });
      return next;
    });
  };

  const startEtsyImport = async (file) => {
    if (isReadOnly) return Upload.LIST_IGNORE;
    if (!startDate || !endDate) {
      message.warning("Please select start date and end date first");
      return Upload.LIST_IGNORE;
    }

    const runId = importRunRef.current + 1;
    importRunRef.current = runId;
    setEtsyProgress({ stage: "Reading file" });

    try {
      const payload = await parseEtsyFile(
        file,
        startDate,
        endDate,
        (stage, progress = {}) => setEtsyProgress({ stage, ...progress }),
        () => importRunRef.current !== runId,
      );
      if (!payload || importRunRef.current !== runId) return false;

      setEtsyProgress({ stage: "Sending compact rows", ...payload.stats });
      const response = await designerBonusService.aggregateEtsy({
        startDate,
        endDate,
        sourceRowCount: payload.sourceRowCount,
        rows: payload.rows,
      });

      if (importRunRef.current !== runId) return false;
      setBonusData((current) => mergeMarketData(current, "Etsy", responseMarketToRows(response.data?.data, "Etsy"), settings.defaultCoefficients.Etsy));
      setSourceStatus((current) => ({ ...current, Etsy: "ready" }));
      message.success("Etsy sales imported");
    } catch (error) {
      if (importRunRef.current === runId) {
        setSourceStatus((current) => ({ ...current, Etsy: "error" }));
        message.error(error.response?.data?.message || error.message || "Cannot process Etsy file");
      }
    } finally {
      if (importRunRef.current === runId) setEtsyProgress(null);
    }
    return false;
  };

  const cancelEtsyImport = () => {
    importRunRef.current += 1;
    setEtsyProgress(null);
  };

  const exportExcel = () => {
    const workbook = XLSX.utils.book_new();

    const summaryExportRows = summaryRows.map((row) => {
      const summaryRow = {
        Designer: row.designerCode,
        "Total Sale": row.totalSale,
      };
      months.forEach((monthKey) => {
        summaryRow[`${monthKey} Bonus`] = row[`${monthKey}Bonus`];
      });
      summaryRow["Total Payout"] = row.totalBonus;
      return summaryRow;
    });

    const totalExportRow = {
      Designer: "Total payout by month",
      "Total Sale": summaryTotals.totalSale,
    };
    months.forEach((monthKey) => {
      totalExportRow[`${monthKey} Bonus`] = summaryTotals[`${monthKey}Bonus`];
    });
    totalExportRow["Total Payout"] = summaryTotals.totalBonus;
    summaryExportRows.push(totalExportRow);

    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summaryExportRows), "Summary");

    MARKETS.forEach((market) => {
      const rows = summaryRows
        .map((summaryRow) => summaryRow.designerCode)
        .filter((designerCode) => bonusData[designerCode]?.[market])
        .map((designerCode) => {
          const result = calculateMarketDesigner(bonusData, designerCode, market, months, settings, calculationPolicyVersion);
          const row = {
            Designer: designerCode,
            "Policy Version": calculationPolicyVersion,
            Threshold: settings.thresholds[market],
            "Period Sale": result.periodSale,
            "Eligible Sale": result.periodEligibleSale,
            "Eligible Months": result.eligibleMonthCount,
            Eligible: result.eligible ? "Yes" : "No",
            Bonus: result.bonus,
          };
          months.forEach((monthKey) => {
            const cell = getCell(bonusData, designerCode, market, monthKey);
            const monthResult = calculateMarketMonthResult(
              bonusData,
              designerCode,
              market,
              monthKey,
              months,
              settings,
              calculationPolicyVersion,
            );
            row[`${monthKey} Sale`] = cell.sale;
            row[`${monthKey} Eligible Sale`] = monthResult.eligibleSale;
            row[`${monthKey} Coefficient`] = cell.coefficient;
            row[`${monthKey} Bonus`] = monthResult.bonus;
          });
          return row;
        });
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), market);
    });

    XLSX.writeFile(workbook, `designer_bonus_${startDate}_${endDate}.xlsx`);
  };

  const marketColumns = (market) => [
    {
      title: "Designer",
      dataIndex: "designerCode",
      fixed: "left",
      width: 96,
      render: (value) => <Tag color="blue">{value}</Tag>,
    },
    ...months.map((monthKey) => ({
      title: (
        <Space direction="vertical" size={2} style={{ width: "100%" }}>
          <Text strong>{monthKey}</Text>
          <div className="designer-bonus-cell-labels">
            <span>Sale</span>
            <span>Coefficient</span>
          </div>
        </Space>
      ),
      dataIndex: monthKey,
      width: 270,
      render: (_, record) => {
        const cell = getCell(bonusData, record.designerCode, market, monthKey);
        const quantityStats = getQuantityStats(bonusData, record.designerCode, market, monthKey);
        const monthResult = calculateMarketMonthResult(
          bonusData,
          record.designerCode,
          market,
          monthKey,
          months,
          settings,
          calculationPolicyVersion,
        );
        return (
          <div>
            <div className="designer-bonus-cell">
              <InputNumber disabled={isReadOnly} min={0} precision={0} value={cell.sale} onChange={(value) => updateCell(record.designerCode, market, monthKey, "sale", value)} />
              <InputNumber disabled={isReadOnly} min={0} precision={2} value={cell.coefficient} onChange={(value) => updateCell(record.designerCode, market, monthKey, "coefficient", value)} />
            </div>
            {calculationPolicyVersion >= MONTHLY_THRESHOLD_POLICY_VERSION && (
              <div className="designer-bonus-quantity-stats">
                <Tag color={monthResult.eligibleSale > 0 ? "green" : "default"}>Bonus Units {numberFormat(monthResult.eligibleSale)}</Tag>
              </div>
            )}
            {showQuantityStats && (
              <div className="designer-bonus-quantity-stats">
                <Tag color="blue">Total Orders {calculationPolicyVersion >= ORDER_COUNT_POLICY_VERSION ? numberFormat(quantityStats.orders) : "N/A"}</Tag>
                <Tag>Total Quantity {numberFormat(quantityStats.grossOrderedQuantity)}</Tag>
                <Tag color="green">Eligible Quantity {numberFormat(quantityStats.eligibleSaleQuantity)}</Tag>
              </div>
            )}
          </div>
        );
      },
    })),
    {
      title: "Period Sale",
      width: 110,
      render: (_, record) => calculateMarketDesigner(bonusData, record.designerCode, market, months, settings, calculationPolicyVersion).periodSale,
    },
    {
      title: "Bonus Units",
      width: 110,
      render: (_, record) => calculateMarketDesigner(bonusData, record.designerCode, market, months, settings, calculationPolicyVersion).periodEligibleSale,
    },
    {
      title: "Status",
      width: 90,
      render: (_, record) => {
        const result = calculateMarketDesigner(bonusData, record.designerCode, market, months, settings, calculationPolicyVersion);
        return result.eligible
          ? <Tag color="green">{calculationPolicyVersion >= MONTHLY_THRESHOLD_POLICY_VERSION ? `${result.eligibleMonthCount}/${months.length} months` : "Bonus (legacy)"}</Tag>
          : <Tag>0</Tag>;
      },
    },
    {
      title: "Bonus (VNĐ)",
      width: 130,
      align: "right",
      render: (_, record) => currency(calculateMarketDesigner(bonusData, record.designerCode, market, months, settings, calculationPolicyVersion).bonus),
    },
  ];

  const marketRows = (market) => designers
    .filter((designerCode) => visibleDesignerCodes.has(designerCode))
    .filter((designerCode) => bonusData[designerCode]?.[market])
    .map((designerCode) => ({ key: `${market}-${designerCode}`, designerCode }));

  const summaryColumns = [
    {
      title: "Designer",
      dataIndex: "designerCode",
      fixed: "left",
      width: 110,
      render: (value) => <Tag color="blue">{value}</Tag>,
    },
    { title: "Total Sale", dataIndex: "totalSale", align: "right", width: 110, render: numberFormat },
    ...months.map((monthKey) => ({
      title: monthKey,
      align: "right",
      width: 230,
      render: (_, record) => {
        const stats = getDesignerQuantityStats(bonusData, record.designerCode, monthKey);
        return (
          <div className="designer-bonus-summary-month">
            <Text strong>{currency(record[`${monthKey}Bonus`])}</Text>
            {showQuantityStats && (
              <div className="designer-bonus-quantity-stats">
                <Tag color="blue">Total Orders {calculationPolicyVersion >= ORDER_COUNT_POLICY_VERSION ? numberFormat(stats.orders) : "N/A"}</Tag>
                <Tag>Total Quantity {numberFormat(stats.grossOrderedQuantity)}</Tag>
                <Tag color="green">Eligible Quantity {numberFormat(stats.eligibleSaleQuantity)}</Tag>
              </div>
            )}
          </div>
        );
      },
    })),
    {
      title: "Total Payout (VNĐ)",
      dataIndex: "totalBonus",
      align: "right",
      fixed: "right",
      width: 160,
      render: currency,
    },
  ];

  const renderDesignerBreakdown = (record) => {
    const breakdownColumns = [
      {
        title: "",
        dataIndex: "market",
        fixed: "left",
        width: 160,
        render: (market) => <span className="designer-bonus-breakdown-market">↳ Bonus {market}</span>,
      },
      ...months.map((monthKey) => ({
        title: monthKey,
        align: "right",
        width: 140,
        render: (_, row) => currency(row[`${monthKey}Bonus`]),
      })),
      {
        title: "Total",
        dataIndex: "totalBonus",
        align: "right",
        fixed: "right",
        width: 150,
        render: (value) => <Text strong>{currency(value)}</Text>,
      },
    ];

    const breakdownRows = MARKETS.map((market) => {
      const row = {
        key: `${record.designerCode}-${market}`,
        market,
        totalBonus: record[`${market}Bonus`],
      };

      months.forEach((monthKey) => {
        row[`${monthKey}Bonus`] = calculateMarketMonthBonus(bonusData, record.designerCode, market, monthKey, months, settings, calculationPolicyVersion);
      });

      return row;
    });

    return (
      <div className="designer-bonus-breakdown">
        <Table
          className="designer-bonus-breakdown-table"
          size="small"
          rowKey="key"
          columns={breakdownColumns}
          dataSource={breakdownRows}
          pagination={false}
          scroll={{ x: Math.max(760, months.length * 140 + 310) }}
        />
      </div>
    );
  };

  const renderDesignerTotalSummary = () => (
    <Table.Summary fixed>
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={2} fixed="left">
          <Text strong>Total payout by month</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2} align="right">
          <Text strong>{numberFormat(summaryTotals.totalSale)}</Text>
        </Table.Summary.Cell>
        {months.map((monthKey, index) => (
          <Table.Summary.Cell key={monthKey} index={index + 3} align="right">
            <Text strong>{moneyVnd(summaryTotals[`${monthKey}Bonus`])}</Text>
          </Table.Summary.Cell>
        ))}
        <Table.Summary.Cell index={months.length + 2} align="right" fixed="right">
          <Text strong>{moneyVnd(summaryTotals.totalBonus)}</Text>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  );

  return (
    <div className="designer-bonus-page">
      <Row justify="space-between" align="middle" gutter={[16, 16]}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <CalculatorOutlined /> Designer Bonus
          </Title>
          <Space size={8} wrap>
            {savedCalculation && <Tag color={savedCalculation.status === "APPROVED" ? "green" : savedCalculation.status === "PENDING_APPROVAL" ? "gold" : "blue"}>{savedCalculation.name} · {savedCalculation.status}</Tag>}
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Button icon={<PlusOutlined />} onClick={newCalculation}>
              New Calculation
            </Button>
            <Button icon={<FolderOpenOutlined />} onClick={openSavedCalculations} loading={savedCalculationsLoading}>
              Open Saved Calculations
            </Button>
            <Button icon={<SaveOutlined />} onClick={openSaveDialog} loading={savingCalculation} disabled={isReadOnly} type="primary">
              Save
            </Button>
            <Button onClick={submitCalculation} loading={submittingCalculation} disabled={!savedCalculation?.id || savedCalculation.status !== "DRAFT"}>
              Submit Snapshot
            </Button>
            <Button icon={<ReloadOutlined />} onClick={loadSales} loading={loading} disabled={isReadOnly}>
              Load Amazon/Website
            </Button>
            <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={startEtsyImport} disabled={isReadOnly}>
              <Button icon={<UploadOutlined />} disabled={isReadOnly}>Import Etsy</Button>
            </Upload>
            <Button icon={<DownloadOutlined />} onClick={exportExcel} disabled={!summaryRows.length}>
              Export
            </Button>
          </Space>
        </Col>
      </Row>

      <Form layout="vertical" component={false}>
        <Row gutter={[16, 4]} style={{ marginTop: 16 }}>
          <Col xs={24} md={8}>
            <Form.Item label="Date range">
              <DatePicker.RangePicker
                value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null}
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
                disabled={isReadOnly}
                ranges={{
                  "First 6 Months (Jan - Jun)": [dayjs(`${currentYear}-01-01`), dayjs(`${currentYear}-06-30`)],
                  "Last 6 Months (Jul - Dec)": [dayjs(`${currentYear}-07-01`), dayjs(`${currentYear}-12-31`)],
                  "Q1 (Jan - Mar)": [dayjs(`${currentYear}-01-01`), dayjs(`${currentYear}-03-31`)],
                  "Q2 (Apr - Jun)": [dayjs(`${currentYear}-04-01`), dayjs(`${currentYear}-06-30`)],
                  "Q3 (Jul - Sep)": [dayjs(`${currentYear}-07-01`), dayjs(`${currentYear}-09-30`)],
                  "Q4 (Oct - Dec)": [dayjs(`${currentYear}-10-01`), dayjs(`${currentYear}-12-31`)],
                  "Full Year (Jan - Dec)": [dayjs(`${currentYear}-01-01`), dayjs(`${currentYear}-12-31`)],
                }}
                onChange={(dates) => {
                  setStartDate(dates?.[0] ? dates[0].format("YYYY-MM-DD") : "");
                  setEndDate(dates?.[1] ? dates[1].format("YYYY-MM-DD") : "");
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Exchange rate">
              <InputNumber disabled={isReadOnly} min={1} precision={0} style={{ width: "100%" }} value={settings.exchangeRate} onChange={(value) => updateSetting("exchangeRate", value)} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 4]}>
          {MARKETS.map((market) => (
            <Col xs={12} md={4} key={market}>
              <Form.Item layout="vertical" label={`${market} threshold`}>
                <InputNumber disabled={isReadOnly} min={0} precision={0} style={{ width: "100%" }} value={settings.thresholds[market]} onChange={(value) => updateSetting(market, value)} />
              </Form.Item>
            </Col>
          ))}
        </Row>
        <Row gutter={[16, 4]}>
          {MARKETS.map((market) => (
            <Col xs={12} md={4} key={`${market}-coefficient`}>
              <Form.Item layout="vertical" label={`${market} default coefficient`}>
                <InputNumber disabled={isReadOnly} min={0} precision={2} style={{ width: "100%" }} value={settings.defaultCoefficients[market]} onChange={(value) => updateDefaultCoefficient(market, value)} />
              </Form.Item>
            </Col>
          ))}
        </Row>
        <Row gutter={[16, 4]} style={{ marginBottom: 30 }}>
          <Col>
            <Button icon={<SaveOutlined />} loading={saving} onClick={saveSettings} disabled={isReadOnly}>
              Save Settings
            </Button>
          </Col>
          <Col>
            <Button icon={showQuantityStats ? <EyeInvisibleOutlined /> : <EyeOutlined />} onClick={() => setShowQuantityStats((visible) => !visible)}>
              {showQuantityStats ? "Hide metrics" : "Show metrics"}
            </Button>
          </Col>
        </Row>
      </Form>

      {etsyProgress && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message={`Etsy: ${etsyProgress.stage}`}
          description={`Rows: ${etsyProgress.sourceRowCount || 0}, valid: ${etsyProgress.validRows || 0}, compact: ${etsyProgress.compactRowCount || 0}`}
          action={
            <Button size="small" onClick={cancelEtsyImport}>
              Cancel
            </Button>
          }
        />
      )}

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={6}>
          <Card className="designer-bonus-card designer-bonus-highlight-card orange" bordered={false}>
            <div className="designer-bonus-kpi-label">Total Bonus Payout</div>
            <div className="designer-bonus-kpi-value">{moneyVnd(totalBonus)}</div>
            <div className="designer-bonus-kpi-hint">Total budget required for period</div>
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card className="designer-bonus-card designer-bonus-highlight-card" bordered={false}>
            <div className="designer-bonus-kpi-label">Sales Performance</div>
            <div className="designer-bonus-kpi-value">{currency(totalSales)}</div>
            <div className="designer-bonus-kpi-subvalue">
              {startDate || "-"} to {endDate || "-"}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card className="designer-bonus-card designer-bonus-highlight-card green" bordered={false}>
            <div className="designer-bonus-kpi-label">Highest Bonus Designer</div>
            <div className="designer-bonus-kpi-value">{highestDesigner?.designerCode || "-"}</div>
            <div className="designer-bonus-kpi-subvalue">{highestDesigner ? moneyVnd(highestDesigner.totalBonus) : "0 VNĐ"}</div>
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card className="designer-bonus-card designer-bonus-highlight-card" bordered={false}>
            <div className="designer-bonus-kpi-label">Loaded Sources</div>
            <div className="designer-bonus-kpi-value" style={{ fontSize: 15, lineHeight: 1.8 }}>
              {Object.entries(sourceStatus).map(([source, status]) => (
                <Tag key={source} color={status === "ready" ? "green" : status === "error" ? "red" : "gold"}>
                  {source}
                </Tag>
              ))}
              {Object.keys(sourceStatus).length === 0 && <Text type="secondary">No source loaded</Text>}
            </div>
            <div className="designer-bonus-kpi-hint">Amazon, Website and Etsy import status</div>
          </Card>
        </Col>
      </Row>

      <Tabs
        items={MARKETS.map((market) => ({
          key: market,
          label: market,
          children: (
            <Card className="designer-bonus-panel" bordered={false}>
              <div className="designer-bonus-market-title">
                <Space>
                  <Title level={5} style={{ margin: 0 }}>
                    {market}
                  </Title>
                  <Text type="secondary">
                    Monthly threshold: {numberFormat(settings.thresholds[market])} units. Only units above the threshold receive bonus.
                  </Text>
                </Space>
                <Popconfirm title={`Reset ${market} sales to source values?`} onConfirm={() => resetMarket(market)}>
                  <Button size="small" disabled={isReadOnly}>Reset Sale</Button>
                </Popconfirm>
              </div>
              <Table
                className="designer-bonus-table"
                size="small"
                rowKey="key"
                columns={marketColumns(market)}
                dataSource={marketRows(market)}
                locale={{ emptyText: "No designer exceeded the monthly threshold" }}
                pagination={{ pageSize: 20 }}
                scroll={{ x: Math.max(1100, months.length * 270 + 420) }}
              />
            </Card>
          ),
        }))}
      />

      <Card className="designer-bonus-panel" bordered={false} title="Designer total">
        <Table
          size="small"
          rowKey="designerCode"
          columns={summaryColumns}
          dataSource={summaryRows}
          locale={{ emptyText: "No designer exceeded the monthly threshold" }}
          expandable={{
            expandedRowRender: renderDesignerBreakdown,
            expandRowByClick: true,
          }}
          summary={renderDesignerTotalSummary}
          pagination={{ pageSize: 20 }}
          scroll={{ x: Math.max(1100, months.length * 230 + 380) }}
        />
      </Card>

      <SaveCalculationModal
        open={saveModalOpen}
        initialName={savedCalculation?.name || ""}
        initialDescription={savedCalculation?.description || ""}
        loading={savingCalculation}
        existing={Boolean(savedCalculation?.id)}
        onCancel={() => setSaveModalOpen(false)}
        onSave={saveCalculation}
      />

      <Modal
        open={savedModalOpen}
        title="Open Saved Calculations"
        onCancel={() => setSavedModalOpen(false)}
        footer={null}
        width="96vw"
        styles={{ body: { maxHeight: "calc(100vh - 150px)", overflowY: "auto" } }}
      >
        <Table
          size="small"
          rowKey="id"
          loading={savedCalculationsLoading}
          dataSource={savedCalculations}
          pagination={false}
          onRow={(record) => ({ onClick: () => selectSavedCalculation(record), style: { cursor: "pointer" } })}
          columns={[
            { title: "Name", dataIndex: "name", ellipsis: true },
            { title: "Date range", render: (_, record) => `${record.startDate || "-"} → ${record.endDate || "-"}` },
            {
              title: "Status",
              dataIndex: "status",
              render: (status) => <Tag color={status === "APPROVED" ? "green" : status === "PENDING_APPROVAL" ? "gold" : "blue"}>{status}</Tag>,
            },
            { title: "Created", dataIndex: "createdAt", render: (value) => value ? new Date(value).toLocaleString() : "-" },
            { title: "Updated", dataIndex: "updatedAt", render: (value) => value ? new Date(value).toLocaleString() : "-" },
            { title: "Submitted", dataIndex: "submittedAt", render: (value) => value ? new Date(value).toLocaleString() : "-" },
            { title: "Approved", dataIndex: "approvedAt", render: (value) => value ? new Date(value).toLocaleString() : "-" },
          ]}
        />

        {previewLoading && <Alert type="info" showIcon message="Loading saved calculation..." style={{ marginTop: 16 }} />}
        {previewCalculation && !previewLoading && (
          <>
            <Divider />
            <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
              <Col>
                <Space wrap>
                  <Text strong>{previewCalculation.name}</Text>
                  <Tag color={previewCalculation.status === "APPROVED" ? "green" : previewCalculation.status === "PENDING_APPROVAL" ? "gold" : "blue"}>{previewCalculation.status}</Tag>
                  <Text type="secondary">{previewCalculation.startDate || "-"} → {previewCalculation.endDate || "-"}</Text>
                </Space>
              </Col>
              <Col>
                <Space>
                  {previewCalculation.status === "DRAFT" && <Button type="primary" onClick={openPreviewForEdit}>Open/Edit</Button>}
                  {previewCalculation.status === "PENDING_APPROVAL" && canApprove && (
                    <Button type="primary" onClick={approvePreviewCalculation} loading={approvingCalculation}>Approve</Button>
                  )}
                </Space>
              </Col>
            </Row>
            <SavedCalculationTables data={previewCalculation.data} showQuantityStats={showQuantityStats} />
          </>
        )}
      </Modal>
    </div>
  );
}

export default DesignerBonus;
